import {
  defineMessages,
  FormattedMessage,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { Easing, FadeIn, FadeInDown, SlideInDown } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useStyle } from 'react-native-style-utilities';

import { theme } from '@garnish/constants';
import { BodyText, Button, DisplayText, useResponsive } from '@garnish/core';

import { OrderState } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Draws its own backdrop and panel instead of opening a React Native modal, so
 * it can render inside a native modal route without a second iOS presentation.
 */
export const GroupOrderTerminalStateModal = (props: Props) => {
  const { hasCurrentUserItems, hostDisplayName, orderChannel, orderState, onDismiss } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();
  const { bottom: safeAreaBottomInset } = useSafeAreaInsets();

  // ─── Derived ───────────────────────────────────────────────────────────────

  const isSheet = currentBreakpoint.isXS;

  const { headline, body } = getTerminalStateText({
    formatMessage,
    hasItems: String(hasCurrentUserItems),
    hostDisplayName,
    orderChannel,
    orderState,
  });

  // ─── Styles ────────────────────────────────────────────────────────────────

  const containerStyles = [
    styles.container,
    isSheet ? styles.containerSheet : styles.containerDialog,
  ];

  const sheetDynamicStyles = useStyle(
    () => (isSheet ? { paddingBottom: theme.spacing['6'] + safeAreaBottomInset } : undefined),
    [isSheet, safeAreaBottomInset],
  );

  const panelStyles = [
    styles.panel,
    isSheet ? styles.panelSheet : styles.panelDialog,
    sheetDynamicStyles,
  ];

  // ───────────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <Animated.View entering={BACKDROP_ENTERING} style={styles.backdrop}>
        <TouchableOpacity
          accessibilityLabel={formatMessage(messages.dismissAccessibilityLabel)}
          accessibilityRole="button"
          activeOpacity={1}
          style={styles.backdropPressable}
          onPress={onDismiss}
        />
      </Animated.View>

      <Animated.View
        aria-label={headline}
        aria-modal
        role="dialog"
        entering={isSheet ? SHEET_ENTERING : DIALOG_ENTERING}
        style={panelStyles}
      >
        <DisplayText sizeMatch={['24', '32']} style={styles.headline}>
          {headline}
        </DisplayText>

        <BodyText sizeMatch={['14', '16']} style={styles.body}>
          {body}
        </BodyText>

        <Button palette="primary" size="large" onPress={onDismiss}>
          <FormattedMessage {...messages.gotIt} />
        </Button>
      </Animated.View>
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getTerminalStateText(params: Params) {
  const { formatMessage, hasItems, hostDisplayName, orderChannel, orderState } = params;

  const isCompleted = orderState === OrderState.Complete;

  if (isCompleted && hostDisplayName) {
    return {
      headline: formatMessage(messages.completedHeadline, { host_display_name: hostDisplayName }),
      body: formatMessage(messages.completedBody, {
        has_items: hasItems,
        host_display_name: hostDisplayName,
        order_channel: orderChannel,
      }),
    };
  }

  if (isCompleted) {
    return {
      headline: formatMessage(messages.completedHeadlineWithoutHost),
      body: formatMessage(messages.completedBodyWithoutHost, {
        has_items: hasItems,
        order_channel: orderChannel,
      }),
    };
  }

  if (hostDisplayName) {
    return {
      headline: formatMessage(messages.canceledHeadline, { host_display_name: hostDisplayName }),
      body: formatMessage(messages.canceledBody),
    };
  }

  return {
    headline: formatMessage(messages.canceledHeadlineWithoutHost),
    body: formatMessage(messages.canceledBody),
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly completedHeadline: { readonly host_display_name: MessageValue };
  readonly completedBody: {
    readonly has_items: string;
    readonly host_display_name: MessageValue;
    readonly order_channel: string;
  };
  readonly canceledHeadline: { readonly host_display_name: MessageValue };
  readonly canceledBody: NoMessageValues;
  readonly completedHeadlineWithoutHost: NoMessageValues;
  readonly completedBodyWithoutHost: { readonly has_items: string; readonly order_channel: string };
  readonly canceledHeadlineWithoutHost: NoMessageValues;
  readonly gotIt: NoMessageValues;
  readonly dismissAccessibilityLabel: NoMessageValues;
}>({
  completedHeadline: {
    defaultMessage: '{host_display_name} submitted the group order',
    description: 'Group Order | Completed modal headline',
  },
  completedBody: {
    defaultMessage:
      '{has_items, select, true {Your items were included. } other {}}Reach out to {host_display_name} for {order_channel, select, delivery {delivery} outpost {outpost} other {pickup}} details.',
    description: 'Group Order | Completed modal body',
  },
  canceledHeadline: {
    defaultMessage: "{host_display_name}'s group order has ended",
    description: 'Group Order | Canceled modal headline',
  },
  canceledBody: {
    defaultMessage: 'Your items were removed. Nothing was charged to you.',
    description: 'Group Order | Canceled modal body',
  },
  completedHeadlineWithoutHost: {
    defaultMessage: 'The group order was submitted',
    description: 'Group Order | Completed modal headline without a host name',
  },
  completedBodyWithoutHost: {
    defaultMessage:
      '{has_items, select, true {Your items were included. } other {}}Reach out to the host for {order_channel, select, delivery {delivery} outpost {outpost} other {pickup}} details.',
    description: 'Group Order | Completed modal body without a host name',
  },
  canceledHeadlineWithoutHost: {
    defaultMessage: 'This group order has ended',
    description: 'Group Order | Canceled modal headline without a host name',
  },
  gotIt: {
    defaultMessage: 'Got it',
    description: 'Group Order | Terminal state modal CTA',
  },
  dismissAccessibilityLabel: {
    defaultMessage: 'Dismiss',
    description: 'Group Order | Terminal state modal | Dismiss backdrop accessibility label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ENTRANCE_EASING = Easing.out(Easing.cubic);

const BACKDROP_ENTERING = FadeIn.duration(theme.transitions.long);
const SHEET_ENTERING = SlideInDown.duration(theme.transitions.long).easing(ENTRANCE_EASING);
const DIALOG_ENTERING = FadeInDown.duration(theme.transitions.long).easing(ENTRANCE_EASING);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  containerSheet: {
    justifyContent: 'flex-end',
  },
  containerDialog: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing['10'],
  },
  backdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  backdropPressable: {
    flex: 1,
  },
  panel: {
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  panelSheet: {
    width: '100%',
    borderTopLeftRadius: theme.radius.large,
    borderTopRightRadius: theme.radius.large,
    paddingTop: theme.spacing['8'],
    paddingHorizontal: theme.spacing['4'],
  },
  panelDialog: {
    width: 584,
    maxWidth: '100%',
    borderRadius: theme.radius.large,
    paddingVertical: theme.spacing['8'],
    paddingHorizontal: theme.spacing['6'],
    boxShadow: '0px 12px 48px 8px rgba(14, 21, 14, 0.12)',
  },
  headline: {
    marginBottom: theme.spacing['4'],
  },
  body: {
    marginBottom: theme.spacing['6'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  hasCurrentUserItems: boolean;
  hostDisplayName: string | undefined;
  orderChannel: OrderChannel;
  orderState: OrderState.Complete | OrderState.Discarded;
  onDismiss: () => void;
};

type Params = {
  formatMessage: ReturnType<typeof useIntl>['formatMessage'];
  hasItems: string;
  hostDisplayName: string | undefined;
  orderChannel: OrderChannel;
  orderState: OrderState.Complete | OrderState.Discarded;
};

type OrderChannel = 'delivery' | 'outpost' | 'pickup';
